import {
  ChangeEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Button,
  Input,
  Select,
  Spin,
} from 'antd';
import { ArrowLeftOutlined, FilePdfOutlined, MailOutlined } from '@ant-design/icons';
import { PatientStatus, PATIENT_STATUSES, REQUEST_STATUSES } from 'stores/common';
import { useAppDispatch, useAppSelector } from 'stores/hooks';
import { useCurrentPatient } from 'stores/patients/patientsHooks';
import {
  getUpdatePatientRequestStatus,
  resetCurrentPatient,
  updatePatientAsync,
} from 'stores/patients/patientsSlice';
import Title from 'components/layout/Title';
import Card from 'components/layout/Card';
import InformationItem from 'components/informations/InformationItem';
import CERFAModal from 'components/cerfa/CERFAModal';
import { getUser } from 'stores/auth/authSlice';
import useDebounce from 'hooks/useDebounce';

export default function Patient() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { patientID } = useParams();
  const [open, setOpen] = useState(false);
  const user = useAppSelector(getUser);
  const patchPatientRequestStatus = useAppSelector(getUpdatePatientRequestStatus);
  const [currentPatient, currentPatientRequestStatus] = useCurrentPatient(patientID || '');
  const informations: { name: ReactNode, value: ReactNode }[] = useMemo(
    () => {
      if (!currentPatient) {
        return [];
      }
      const dateOfBirth = dayjs(currentPatient.date_of_birth, 'DD/MM/YYYY');
      const age = Math.floor(dayjs().diff(dateOfBirth, 'years'));
      const thirdMeetingDate = dayjs(
        `${currentPatient.rdv_3___date} ${currentPatient.rdv_3___heure}`,
        'YYYY-MM-DD HH:mm',
      );
      const ppcMeetingDate = currentPatient.rdv_suivi___date ? dayjs(
        `${currentPatient.rdv_suivi___date} ${currentPatient.rdv_suivi___heure}`,
        'YYYY-MM-DD HH:mm',
      ) : null;
      const nextMeeting = thirdMeetingDate.isAfter(dayjs()) ? thirdMeetingDate : ppcMeetingDate;
      const nextMeetingComment = thirdMeetingDate.isAfter(dayjs())
        ? currentPatient.commentaires_rdv_3
        : currentPatient.rdv_suivi___commentaires || '';

      const informationsArray = [
        {
          name: 'Nom',
          value: [currentPatient.firstname, currentPatient.lastname]
            .filter((name) => Boolean(name)).join(' '),
        },
        {
          name: 'Âge - Date de naissance',
          value: dateOfBirth.isValid()
            ? `${age} an${age > 1 ? 's' : ''} - ${dateOfBirth.format('DD/MM/YYYY')}`
            : null,
        },
        {
          name: 'Adresse email',
          value: currentPatient.email,
        },
        {
          name: 'Téléphone',
          value: currentPatient.mobilephone,
        },
        {
          name: 'Code postal',
          value: currentPatient.zip,
        },
        {
          name: 'NIC',
          value: currentPatient.nic,
        },
        {
          name: 'Dossier',
          value: currentPatient.dossier ? (
            <a
              className='underline text-primary'
              href={currentPatient.dossier}
              target='_blank'
              rel='noreferrer noopener'
            >
              Lien dossier
            </a>
          ) : 'Pas de dossier…',
        },
        {
          name: 'Ordonnance',
          value: currentPatient.lien_ordonnance ? (
            <a
              className='underline text-primary'
              href={currentPatient.lien_ordonnance}
              target='_blank'
              rel='noreferrer noopener'
            >
              Lien ordonnance
            </a>
          ) : 'Pas d\'ordonnance…',
        },
        {
          name: 'Statut du rendez-vous',
          value: currentPatient.rdv_3__statut,
        },
      ];

      if (nextMeeting) {
        informationsArray.push(
          {
            name: 'Prochain rendez-vous',
            value: `${nextMeeting.format('D MMMM à HH:mm')}`,
          },
          {
            name: 'Commentaires (rendez-vous)',
            value: nextMeetingComment,
          },
        );
      }

      return informationsArray;
    },
    [currentPatient],
  );
  const setOpenModal = useCallback((isOpen: boolean) => () => setOpen(isOpen), []);
  const handleBackClick = useCallback(() => navigate(-1), [navigate]);
  const onChangeObservations = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      if (currentPatient) {
        dispatch(updatePatientAsync({
          patientID: currentPatient?.idwp,
          observations: event.target.value,
        }));
      }
    },
    [currentPatient, dispatch],
  );
  const onChangePatientStatus = useCallback(
    (status: PatientStatus) => {
      if (currentPatient) {
        dispatch(updatePatientAsync({
          patientID: currentPatient?.idwp,
          status,
        }));
      }
    },
    [currentPatient, dispatch],
  );
  const debouncedOnChangeObservations = useDebounce(onChangeObservations, 500);
  const statusOptions = useMemo(
    () => Object.values(PATIENT_STATUSES).map((value) => ({ value, label: value })),
    [],
  );

  useEffect(() => () => {
    dispatch(resetCurrentPatient());
  }, [dispatch]);

  if (currentPatientRequestStatus === REQUEST_STATUSES.PENDING) {
    return (
      <div className='flex flex-col items-center justify-center grow'>
        <Spin size='large' />
      </div>
    );
  }

  if (!currentPatient) {
    return (
      <div className='flex flex-col items-center justify-center grow'>
        <Title>Impossible de retrouver ce patient…</Title>
        <Button
          onClick={handleBackClick}
          className='!flex !items-center mb-6'
          icon={<ArrowLeftOutlined />}
        >
          <span>Retour</span>
        </Button>
      </div>
    );
  }

  return (
    <div className='flex flex-col grow'>
      <Button
        size='small'
        type='text'
        onClick={handleBackClick}
        className='!w-24 !inline-flex !items-center mb-2'
        icon={<ArrowLeftOutlined />}
      >
        <span>Retour</span>
      </Button>
      <Title main>Informations du patient</Title>
      <Card className='grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2'>
        {informations.map(({ name, value }) => (
          <InformationItem
            name={name}
            value={value}
            key={name?.toString()}
          />
        ))}
      </Card>
      <div className='flex items-center space-x-2'>
        <Title main>Actions</Title>
        {patchPatientRequestStatus === REQUEST_STATUSES.PENDING && (
          <div className='mb-4'>
            <Spin size='small' />
          </div>
        )}
      </div>
      <div className='flex space-x-4'>
        <Select
          placeholder='Changer statut patient'
          defaultValue={currentPatient.rdv_3___statut2}
          onChange={onChangePatientStatus}
          options={statusOptions}
          className='!rounded-full w-52'
        />
        <Button
          type='primary'
          onClick={setOpenModal(true)}
          className='!inline-flex !items-center'
          icon={<FilePdfOutlined />}
        >
          Rédiger le CERFA
        </Button>
        <a
          href={`mailto:${currentPatient?.email}`}
          target='_blank'
          rel='noreferrer noopener'
        >
          <Button
            icon={<MailOutlined />}
            className='!inline-flex !items-center'
          >
            Envoyer un mail
          </Button>
        </a>
        {
          user && (
            <CERFAModal
              open={open}
              onCancel={setOpenModal(false)}
              setOpenModal={setOpen}
              doctor={user}
              patient={currentPatient}
            />
          )
        }
      </div>
      <Card className='flex flex-col mt-6 grow min-h-[180px]'>
        <h3 className='mb-2 font-semibold'>Observations</h3>
        <Input.TextArea
          className='grow'
          placeholder='Commentaires libres…'
          onChange={debouncedOnChangeObservations}
          defaultValue={currentPatient.rdv_3___observations_medecin}
        />
      </Card>
    </div>
  );
}
